import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { switchMap } from 'rxjs';

import { injectTranslate, TranslatePipe } from '@core/i18n-legacy';
import {
  SkillsImportDataFacade,
  SkillsImportEvent,
  SkillsImportProgressTrackService,
} from '@core/tellent-skills-logic';
import {
  ButtonComponent,
  DialogBodyComponent,
  DialogComponentI,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  IconComponent,
  SpinnerComponent,
} from '@core/user-interface-legacy';

import { LoadingGameFrameComponent } from './loading-game-frame/loading-game-frame.component';

type DialogStatus = 'idle' | 'processing' | 'failed';

@Component({
  selector: 'tlc-import-skills-loading-dialog',
  templateUrl: './import-skills-loading-dialog.component.html',
  styleUrl: './import-skills-loading-dialog.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DialogBodyComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
    ButtonComponent,
    IconComponent,
    TranslatePipe,
    LoadingGameFrameComponent,
    SpinnerComponent,
  ],
})
export class ImportSkillsLoadingDialogComponent implements DialogComponentI<
  { id: number; userPrompt: string | null },
  { id: number } | { back: true }
> {
  private readonly t = injectTranslate();
  private readonly dialogRef = inject(DialogRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly facade = inject(SkillsImportDataFacade);
  private readonly progressTrack = inject(SkillsImportProgressTrackService);

  protected readonly status = signal<DialogStatus>('idle');
  protected readonly failureReason = signal<string | null>(null);

  protected readonly gameStartText = this.t(
    'tlc.positions.import_skills_flow.loading_dialog.loading_game.start',
  );
  protected readonly gameTutorialText = this.t(
    'tlc.positions.import_skills_flow.loading_dialog.loading_game.tutorial',
  );
  protected readonly gameOverText = this.t(
    'tlc.positions.import_skills_flow.loading_dialog.loading_game.game_over',
  );
  protected readonly gameScoreText = this.t(
    'tlc.positions.import_skills_flow.loading_dialog.loading_game.score',
  );

  public setDialogData(
    { id, userPrompt }: { id: number; userPrompt: string | null },
    _triggerUpdate: boolean,
  ): void {
    this.facade
      .submitImport(id, userPrompt)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => this.progressTrack.track(id)),
      )
      .subscribe({
        next: ({ event }) => {
          if (event === SkillsImportEvent.Processing) {
            this.status.set('processing');
          } else if (event === SkillsImportEvent.Done) {
            this.dialogRef.close({ id });
          }
        },
        error: ({ reason }: { reason?: string }) => {
          this.status.set('failed');
          this.failureReason.set(reason ?? null);
        },
      });
  }

  public back(): void {
    this.dialogRef.close({ back: true });
  }
}
